import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { Box, useTheme } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';

interface CopyCellProps {
  text?: string | number | null;
  displayText?: string;
  shorten?: boolean;
  start?: number;
  end?: number;
  copiedText?: string;
  minWidth?: number | string;
  style?: CSSProperties;
  className?: string;
}

function shortText(value: string, start = 6, end = 4) {
  if (!value) return '-';
  if (value.length <= start + end + 3) return value;
  return `${value.slice(0, start)}...${value.slice(-end)}`;
}

export default function CopyCell({
  text,
  displayText,
  shorten = true,
  start = 6,
  end = 4,
  copiedText = '已复制',
  minWidth,
  style,
  className,
}: CopyCellProps) {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  const rawText = useMemo(() => String(text ?? ''), [text]);
  const defaultColor = theme.palette.mode === 'dark' ? '#FFFFFF' : 'inherit';

  const finalDisplayText = useMemo(() => {
    if (!rawText) return '-';
    if (displayText) return displayText;
    return shorten ? shortText(rawText, start, end) : rawText;
  }, [rawText, displayText, shorten, start, end]);

  const handleCopy = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      event.stopPropagation();

      if (!rawText) return;

      navigator.clipboard.writeText(rawText).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      });
    },
    [rawText]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleCopy(event);
      }
    },
    [handleCopy]
  );

  return (
    <Box
      component="span"
      className={['copy-cell', copied && 'copied', className].filter(Boolean).join(' ')}
      style={style}
      role="button"
      tabIndex={0}
      onClick={handleCopy}
      onKeyDown={handleKeyDown}
      title={rawText || '-'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 0.5,
        height: 20,
        lineHeight: '20px',
        minWidth: minWidth ?? (shorten ? 112 : undefined),
        cursor: rawText ? 'pointer' : 'default',
        color: copied ? '#81C995' : defaultColor,
        fontFamily: 'Roboto Mono, monospace',
        fontSize: 13,
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        '& .MuiSvgIcon-root': {
          fontSize: 14,
          width: 14,
          height: 14,
          flexShrink: 0,
        },
        '&:hover': {
          color: rawText ? '#BB86FC' : 'inherit',
        },
      }}
    >
      {copied ? (
        <>
          <DoneIcon sx={{ fontSize: 16 }} />
          {copiedText}
        </>
      ) : (
        finalDisplayText
      )}
    </Box>
  );
}
